<template>
  <AIAssistantBaseChatResponse :loading="message.stream.loading">
    <div class="tw:grid tw:gap-4">
      <AIAssistantBaseChatMarkdown
        v-if="showStreamingMessage"
        :source="message.stream.message"
      />

      <AIAssistantBaseChatType
        v-else
        @complete="onChatComplete"
      >
        {{ chatMessage }}
      </AIAssistantBaseChatType>

      <AIAssistantBaseChatTransition
        v-if="showActions"
        appear
        class="tw:grid tw:gap-4"
      >
        <slot name="controls" />

        <AIAssistantActions
          :copy-text="message.stream.booleanQuery || message.stream.message"
          :data-index="actionsIndex"
        >
          <AIFeedback
            :ai-feature-configuration-id="
              message.stream.data.ai_feature_configuration_id!
            "
            :ai-input="{ input: message.stream.input }"
            :ai-result="{
              result: message.stream.booleanQuery || message.stream.message,
              chatGroupId: message.stream.data.group_id,
            }"
            show-component
            :source="message.stream.data.source"
          />
        </AIAssistantActions>
      </AIAssistantBaseChatTransition>
    </div>
  </AIAssistantBaseChatResponse>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import AIAssistantActions from "shared/components/AI/AIAssistant/AIAssistantActions.vue";
import AIAssistantBaseChatMarkdown from "shared/components/AI/AIAssistant/AIAssistantBaseChatMarkdown.vue";
import AIAssistantBaseChatResponse from "shared/components/AI/AIAssistant/AIAssistantBaseChatResponse.vue";
import AIAssistantBaseChatType from "shared/components/AI/AIAssistant/AIAssistantBaseChatType.vue";
import AIFeedback from "shared/components/AI/AIFeedback.vue";
import { type AIAssistantModelMessage } from "shared/composables/useAIAssistant";
import { hasSlotContent } from "shared/helpers/hasSlotContent";

import AIAssistantBaseChatTransition from "./AIAssistantBaseChatTransition.vue";

interface AIAssistantBaseChatModelMessageProps {
  message: AIAssistantModelMessage;
}

const props = defineProps<AIAssistantBaseChatModelMessageProps>();

const slots = defineSlots<{
  controls?(): any;
}>();

const showActions = ref<boolean>(false);

const showStreamingMessage = computed<boolean>(() =>
  Boolean(
    !props.message.stream.json &&
      props.message.stream.message &&
      !props.message.stream.error
  )
);

const chatMessage = computed<string>(() => {
  if (props.message.stream.loading && !props.message.stream.message) {
    return getLocaleText("ai_assistant.just_a_moment");
  }

  if (props.message.stream.rateLimitReached) {
    return getLocaleText("ai_assistant.rate_limit_reached");
  }

  if (props.message.stream.error) {
    return getLocaleText("ai_assistant.error_has_occurred");
  }

  return props.message.stream.message;
});

const actionsIndex = computed<number>(() =>
  slots.controls && hasSlotContent(slots.controls) ? 1 : 0
);

function onChatComplete() {
  if (props.message.stream.error || props.message.stream.loading) return;

  showActions.value = true;
}

watch(
  () => props.message.stream.loading,
  (value) => {
    if (value || !showStreamingMessage.value) return;

    showActions.value = true;
  },
  { immediate: true }
);
</script>
